<template>
  <div class="relative flex flex-col justify-center w-full">
    <form
      class="w-full space-y-6"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
      @keydown.esc.prevent="close"
    >
      <div
        @click="showTooltip = !showTooltip"
        class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
      >
        <h2>{{ item.questionDisplay }}</h2>
        <span v-if="item.required" class="text-ruby">*</span>
        <span class="cursor-pointer" v-if="item.tooltip.length"
          ><InformationCircleIcon class="w-8 h-8 text-gold"
        /></span>
      </div>
      <input
        type="text"
        :placeholder="placeholder"
        v-model="query"
        v-focus
        :name="item.question.key"
        class="flex rounded-lg w-full h-[60px] text-xl border-gold dark:text-white dark:bg-[#1b1c20] dark:border-gold focus:ring-0 border-2 focus focus:border-gold"
        :aria-describedby="label"
      />
      <div
        v-for="result in searchResults"
        :key="result.id"
        @click="setSelected(result)"
        :class="[
          'relative md:w-full block rounded-lg border border-gray-300 bg-white dark:bg-[#1b1c20] shadow-sm px-6 py-4 cursor-pointer hover:border-gold sm:flex sm:justify-between focus:outline-none',
        ]"
        :id="`headlessui-radiogroup-option-${result.id}`"
        role="radio"
        aria-checked="true"
        tabindex="0"
        aria-labelledby="headlessui-label-8"
      >
        <div class="flex items-center">
          <div class="text-sm">
            <p
              id="headlessui-label-8"
              class="text-xl font-medium dark:text-white"
            >
              {{ result.name }}
            </p>
          </div>
        </div>
        <div
          class="absolute border-2 border-transparent rounded-lg pointer-events-none -inset-px"
          :class="{ 'border-gold': selected === result }"
          aria-hidden="true"
        ></div>
      </div>
      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <div class="flex item-center space-x-6">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
          <button
            @click="$emit('close')"
            class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white"></span>
            <BackIcon />
          </button>
        </div>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
            <div class="flex item-center space-x-6">
                <button
                    v-if="!item.required"
                    @click="$emit('skip', item, selected)"
                    class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
                >
            <span class="text-black dark:text-white">{{
                    t("input.skip")
                }}</span>
                    <SkipIcon />
                </button>
                <button
                    @click="$emit('close')"
                    class="inline-flex items-center justify-center space-x-1 font-bold text-sm py-2 text-white rounded-full focus:outline-none"
                >
                    <span class="text-black dark:text-white"></span>
                    <BackIcon />
                </button>
            </div>
        </div>
      </div>
    </form>

    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/solid";
import { computed, reactive, toRefs, watch } from "vue";
import Spinner from "../Spinner";
import { useI18n } from "vue-i18n";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";
import BackIcon from "../icons/BackIcon";

const { debounce } = require("lodash");

export default {
  name: "AutoComplete",
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  components: {
    InformationCircleIcon,
    SkipIcon,
    BackIcon,
    ReturnIcon,
    Spinner,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const answer = computed(() => props.item.answer);

    const state = reactive({
      showTooltip: false,
      showError: false,
      error: "",
      query: answer.value ? answer.value.answer.name : "",
      searchResults: [],
      selected: answer.value ? answer.value.answer : "",
    });

    const next = () => {
      if (
        props.item.required &&
        (state.selected === null ||
          state.selected === "" ||
          state.selected.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.required");
        return;
      }
      emit("next", props.item, state.selected);
    };

    const updatePredictions = async () => {
      const collection = props.item.question.query;
      const query = state.query;
      try {
        const { data } = await axios.post(collection, {
          query,
        });

        state.searchResults = data.data;
      } catch (e) {
        state.searchResults = [];
      }
    };

    watch(
      () => state.query,
      debounce((value) => {
        state.selected = "";
        if (state.selected && value === state.selected.name) return;
        updatePredictions(value);
      }, 250)
    );

    const setSelected = (value) => {
      state.selected = value;
      state.query = value.name;
      state.searchResults = [];
    };

    return {
      t,
      ...toRefs(state),
      next,
      setSelected,
      updatePredictions,
    };
  },
};
</script>
